import { forwardRef, memo, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
// Material UI imports
import { type TooltipProps } from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
// Material Icon imports
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// Skillmore UI Components
import SimpleTooltip from '@empathco/ui-components/src/elements/SimpleTooltip';
// SCSS imports
import { avatar, hidden, disabled as disabledColor } from './SuggestedSkillIcon.module.scss';

type SuggestedSkillIconProps = {
  readonly active?: boolean | null;
  readonly placement?: TooltipProps['placement'];
  readonly className?: string;
  readonly withoutTooltip?: boolean;
  readonly disabled?: boolean | null;
  // for Storybook only
  readonly isOpen?: boolean;
}

const SuggestedSkillIcon = forwardRef<HTMLDivElement, SuggestedSkillIconProps>(({
  active = false,
  placement = 'top',
  className,
  withoutTooltip = false,
  disabled = false,
  isOpen = false
}, ref) => {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);

  const tooltip = useMemo(() => formatMessage({ id: 'common.suggested_skill' }), [formatMessage]);

  useEffect(() => {
    if (isOpen) setOpen(isOpen);
  }, [isOpen]);

  const handleTooltipClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleTooltipOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const classNameFull = clsx(className, {
    [avatar]: active && !disabled,
    [disabledColor]: active && disabled,
    [hidden]: !active
  });

  const icon = active ? (
    <Avatar
        ref={ref}
        variant="circular"
        aria-hidden={active ? undefined : 'true'}
        className={classNameFull}
    >
      <CheckCircleIcon fontSize="inherit"/>
    </Avatar>
  ) : <Box ref={ref} aria-hidden className={classNameFull}/>;

  return (
    <>
      {active && !withoutTooltip ? (
        <SimpleTooltip
            aria-hidden
            open={open}
            onOpen={handleTooltipOpen}
            onClose={handleTooltipClose}
            disableInteractive
            disableFocusListener
            placement={placement}
            title={tooltip}
        >
          {icon}
        </SimpleTooltip>
      ) : icon}
      {active ? (
        <Box sx={visuallyHidden}>
          {tooltip}
        </Box>
      ) : undefined}
    </>
  );
});

SuggestedSkillIcon.displayName = 'SuggestedSkillIcon';

export default memo(SuggestedSkillIcon);
